import { Weddings } from '@bridebook/models';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from '@bridebook/web/lib/utils';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';

/**
 * Hook that provides a mutation to update the email for a guest and their entire team.
 *
 * When the mutation is called:
 * - If the guest is a head guest (no head property), updates the head and all guests that point to them
 * - If the guest is a team member (has head property), updates their head guest and all other team members
 * - Uses a batch write to ensure atomic updates across the team
 * - Follows the same logic as saveGuestAndUpdateGroup in save-guest.ts
 *
 * @returns Mutation object with:
 *   - mutateAsync: Function that takes { guestId: string, email: string }
 *   - isLoading: Boolean indicating if mutation is in progress
 */
export const useUpdateGuestEmail = () => {
  const weddingId = useSelector(getWeddingId);

  const mutation = useMutation({
    mutationFn: async ({ guestId, email }: { guestId: string; email: string }) => {
      const wedding = Weddings._.getById(weddingId);
      const weddingGuests = wedding.Guests;

      await weddingGuests.getById(guestId).update({
        'contacts.email': email,
      });

      return { data: { email }, guestId };
    },
    onSettled: (_mutation) => {
      // There is no need for manual update, because we are using Firebase listener - look for useGuestlistListener
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
  };
};
